import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Create axios instance
const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Setup APIs (for first-time admin creation)
export const setupAPI = {
  getStatus: () => api.get('/setup/status'),
  createAdmin: (data) => api.post('/setup/admin', data),
};

// Auth APIs
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  getVerifiedLandlords: () => api.get('/auth/landlords'),
  googleSession: (sessionId) => {
    const formData = new FormData();
    formData.append('session_id', sessionId);
    return api.post('/auth/google/session', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  getMe: () => api.get('/auth/me'),
  confirmEmail: (token) => api.post(`/auth/confirm-email?token=${encodeURIComponent(token)}`),
  resendConfirmation: () => api.post('/auth/resend-confirmation'),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  verifyResetToken: (token) => api.post(`/auth/verify-reset-token?token=${token}`),
  resetPassword: (token, newPassword) => api.post('/auth/reset-password', { token, new_password: newPassword }),
  getInvitation: (token) => api.get(`/invitations/${token}`),
  getManagerInvitation: (token) => api.get(`/auth/manager-invitation/${token}`),
  getLandlordInvitation: (token) => api.get(`/invitations/landlord/${token}`),
  getPendingTenantInvitation: (token) => api.get(`/landlord/pending-tenants/validate/${token}`),
};

// Manager APIs
export const managerAPI = {
  getDashboard: () => api.get('/manager/dashboard'),
  // Properties
  getProperties: () => api.get('/manager/properties'),
  createProperty: (data) => api.post('/manager/properties', data),
  updateProperty: (id, data) => api.put(`/manager/properties/${id}`, data),
  // Units
  getUnits: () => api.get('/manager/units'),
  createUnit: (data) => api.post('/manager/units', data),
  createUnitsBulk: (data) => api.post('/manager/units/bulk', data),
  updateUnit: (id, data) => api.put(`/manager/units/${id}`, data),
  deleteUnit: (id) => api.delete(`/manager/units/${id}`),
  // Leases
  getLeases: () => api.get('/manager/leases'),
  createLease: (data) => api.post('/manager/leases', data),
  updateLease: (id, data) => api.put(`/manager/leases/${id}`, data),
  deleteLease: (id) => api.delete(`/manager/leases/${id}`),
  downloadLeasePdf: (id) => api.get(`/manager/leases/${id}/pdf`, { responseType: 'blob' }),
  // Tenants
  getTenants: () => api.get('/manager/tenants'),
  inviteTenant: (data) => api.post('/manager/tenants/invite', data),
  removeTenant: (id) => api.delete(`/manager/tenants/${id}`),
  verifyTenant: (id) => api.put(`/manager/tenants/${id}/verify`),
  rejectTenant: (id) => api.delete(`/manager/tenants/${id}/reject`),
  // Payments
  getPayments: () => api.get('/manager/payments'),
  verifyPayment: (id) => api.put(`/manager/payments/${id}/verify`),
  rejectPayment: (id) => api.put(`/manager/payments/${id}/reject`),
  editPayment: (id, data) => api.put(`/manager/payments/${id}/edit`, data),
  downloadReceipt: (id) => api.get(`/manager/payments/${id}/receipt`, { responseType: 'blob' }),
  // Expenses
  getExpenses: (propertyId) => api.get(`/manager/expenses${propertyId ? `?property_id=${propertyId}` : ''}`),
  createExpense: (data) => api.post('/manager/expenses', data),
  editExpense: (id, data) => api.put(`/manager/expenses/${id}`, data),
  deleteExpense: (id) => api.delete(`/manager/expenses/${id}`),
  // Forms
  createForm: (templateId, tenantId) => api.post(`/manager/forms/create?template_id=${templateId}&tenant_id=${tenantId}`),
  getForms: (tenantId) => api.get(`/manager/forms${tenantId ? `?tenant_id=${tenantId}` : ''}`),
  deleteForm: (formId) => api.delete(`/manager/forms/${formId}`),
  getRegistrationLink: () => api.get('/manager/registration-link'),
  // Analytics
  getAnalytics: () => api.get('/manager/analytics'),
};

// Admin APIs
export const adminAPI = {
  getDashboard: () => api.get('/admin/dashboard'),
  getAnalytics: () => api.get('/admin/analytics'),
  getPulse: () => api.get('/admin/pulse'),
  getLandlords: () => api.get('/admin/landlords'),
  getLandlordDetail: (id) => api.get(`/admin/landlords/${id}/detail`),
  getSubscriptions: () => api.get('/admin/subscriptions'),
  getSubscriptionPayments: () => api.get('/admin/subscription-payments'),
  recordSubscriptionPayment: (data) => api.post('/admin/subscription-payments', data),
  refundStripeSubscription: (landlordId) => api.post(`/subscriptions/refund/${landlordId}`),
  getStripeWebhookEvents: (limit = 25) => api.get('/admin/stripe-webhook-events', { params: { limit } }),
  replayStripeWebhookEvent: (eventId) => api.post(`/admin/stripe-webhook-events/${eventId}/replay`),
  getLandlordPayments: (landlordId) => api.get(`/admin/landlords/${landlordId}/payments`),
  // Landlord Invitations
  getLandlordInvitations: () => api.get('/admin/invitations/landlord'),
  createLandlordInvitation: (data) => api.post('/admin/invitations/landlord', data),
  cancelLandlordInvitation: (id) => api.delete(`/admin/invitations/landlord/${id}`),
  resendLandlordInvitation: (id) => api.post(`/admin/invitations/landlord/${id}/resend`),
  // Settings
  getPaymentSettings: () => api.get('/admin/settings/payment'),
  updatePaymentSettings: (settings) => api.put('/admin/settings/payment', settings),
  getNotificationSettings: () => api.get('/admin/settings/notifications'),
  updateNotificationSettings: (settings) => api.put('/admin/settings/notifications', settings),
  // Support
  getSupportTickets: (status) => api.get(`/admin/support-tickets${status ? `?status=${status}` : ''}`),
  getSupportTicket: (id) => api.get(`/admin/support-tickets/${id}`),
  replyToTicket: (id, data) => api.post(`/admin/support-tickets/${id}/reply`, typeof data === 'string' ? { message: data } : data),
  createOutreachTicket: (data) => api.post('/admin/support-tickets/new', data),
  getSupportRecipients: (role) => api.get(`/admin/support-recipients?role=${role}`),
  updateTicketStatus: (id, status) => api.put(`/admin/support-tickets/${id}/status?status=${status}`),
  uploadSupportAttachment: (formData) => api.post('/messages/upload-attachment', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  // Ratings
  getRatings: (status) => api.get(`/admin/ratings${status ? `?status=${status}` : ''}`),
  approveRating: (id) => api.put(`/admin/ratings/${id}/approve`),
  rejectRating: (id, reason) => api.put(`/admin/ratings/${id}/reject${reason ? `?reason=${encodeURIComponent(reason)}` : ''}`),
  // Landlord Management
  deactivateLandlord: (id) => api.delete(`/admin/landlords/${id}`),
  // Migrations
  backfillLandlordCodes: () => api.post('/admin/migrate/backfill-landlord-codes'),
  processTrialExpirations: () => api.post('/admin/process-trial-expirations'),
  runNotificationsNow: () => api.post('/admin/notifications/run-now'),
  getJobRuns: (limit = 20) => api.get('/admin/job-runs', { params: { limit } }),
  // Pricing & Promo Codes
  getPricing: () => api.get('/admin/pricing'),
  updatePricing: (data) => api.put('/admin/pricing', data),
  getPromoCodes: () => api.get('/admin/promo-codes'),
  createPromoCode: (data) => api.post('/admin/promo-codes', data),
  updatePromoCode: (id, data) => api.put(`/admin/promo-codes/${id}`, data),
  deletePromoCode: (id) => api.delete(`/admin/promo-codes/${id}`),
};

// Landlord APIs
export const landlordAPI = {
  getDashboard: () => api.get('/landlord/dashboard'),
  
  // Subscription Status
  getSubscriptionStatus: () => api.get('/landlord/subscription/status'),
  
  // Properties
  getProperties: () => api.get('/landlord/properties'),
  createProperty: (data) => api.post('/landlord/properties', data),
  updateProperty: (id, data) => api.put(`/landlord/properties/${id}`, data),
  deleteProperty: (id) => api.delete(`/landlord/properties/${id}`),
  uploadPropertyImage: (formData) => api.post('/landlord/properties/upload-image', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  
  // Units
  getUnits: (propertyId) => api.get(`/landlord/units${propertyId ? `?property_id=${propertyId}` : ''}`),
  createUnit: (data) => api.post('/landlord/units', data),
  createUnitsBulk: (data) => api.post('/landlord/units/bulk', data),
  updateUnit: (id, data) => api.put(`/landlord/units/${id}`, data),
  deleteUnit: (id) => api.delete(`/landlord/units/${id}`),
  
  // Tenant Invitations
  getInvitations: () => api.get('/landlord/invitations'),
  createInvitation: (data) => api.post('/landlord/invitations', data),
  cancelInvitation: (id) => api.delete(`/landlord/invitations/${id}`),
  
  // Property Managers
  getManagers: () => api.get('/landlord/managers'),
  getPendingManagers: () => api.get('/landlord/managers/pending'),
  getManagerInvitations: () => api.get('/landlord/managers/invitations'),
  inviteManager: (data) => api.post('/landlord/managers/invite', data),
  cancelManagerInvitation: (id) => api.delete(`/landlord/managers/invitations/${id}`),
  verifyManager: (id, data) => api.put(`/landlord/managers/${id}/verify`, data),
  rejectManager: (id) => api.delete(`/landlord/managers/${id}/reject`),
  updateManager: (id, data) => api.put(`/landlord/managers/${id}`, data),
  revokeManager: (id) => api.delete(`/landlord/managers/${id}`),
  
  // Stripe Subscriptions (Path B: one-time checkout)
  getStripePlans: () => api.get('/subscriptions/plans'),
  createStripeCheckout: (plan, originUrl, promoCode) => api.post('/subscriptions/checkout', { plan, origin_url: originUrl, promo_code: promoCode || null }),
  validatePromoCode: (code, plan) => api.post('/subscriptions/validate-promo', { code, plan }),
  getStripeSubscriptionStatus: () => api.get('/subscriptions/status'),
  cancelStripeSubscription: () => api.post('/subscriptions/cancel'),
  getStripeCheckoutStatus: (sessionId) => api.get(`/subscriptions/checkout-status/${sessionId}`),

  // Leases
  getLeases: (status) => api.get(`/landlord/leases${status ? `?status=${status}` : ''}`),
  createLease: (data) => api.post('/landlord/leases', data),
  updateLease: (id, data) => api.put(`/landlord/leases/${id}`, data),
  activateLease: (id) => api.put(`/landlord/leases/${id}/activate`),
  forceActivateLease: (id, reason) => api.put(`/landlord/leases/${id}/force-activate`, { reason }),
  terminateLease: (id) => api.put(`/landlord/leases/${id}/terminate`),
  deleteLease: (id) => api.delete(`/landlord/leases/${id}`),
  bulkDeleteLeases: (filterType, leaseIds) => api.post('/landlord/leases/bulk-delete', { filter_type: filterType, lease_ids: leaseIds }),
  archiveAndRenew: (id, data) => api.post(`/landlord/leases/${id}/archive-and-renew`, data),
  getLeaseHistory: (id) => api.get(`/landlord/leases/${id}/history`),
  exportLeasePdf: (id, opts = {}) => {
    const params = new URLSearchParams();
    if (opts.share_with_tenant === false) params.set('share_with_tenant', 'false');
    const qs = params.toString();
    return api.get(`/landlord/leases/${id}/export-pdf${qs ? `?${qs}` : ''}`, { responseType: 'blob' });
  },
  signLeaseAsLandlord: (id, signatureImage) => api.post(`/landlord/leases/${id}/sign`, { signature_image: signatureImage }),
  
  // Tenants
  getTenants: () => api.get('/landlord/tenants'),
  getAvailableTenants: () => api.get('/landlord/available-tenants'),
  getPendingTenants: () => api.get('/landlord/tenants/pending'),
  verifyTenant: (tenantId) => api.put(`/landlord/tenants/${tenantId}/verify`),
  rejectTenant: (tenantId) => api.delete(`/landlord/tenants/${tenantId}/reject`),
  mergeTenants: (data) => api.post('/landlord/tenants/merge', data),
  removeTenant: (tenantId) => api.delete(`/landlord/tenants/${tenantId}`),
  offboardTenant: (tenantId, payload) => api.post(`/landlord/tenants/${tenantId}/offboard`, payload),
  getTenantHistory: () => api.get('/landlord/tenant-history'),
  getTenantHistoryRecord: (historyId) => api.get(`/landlord/tenant-history/${historyId}`),
  downloadMoveOutStatement: (historyId) => api.get(`/landlord/tenant-history/${historyId}/pdf`, { responseType: 'blob' }),
  reonboardTenant: (historyId) => api.post(`/landlord/tenant-history/${historyId}/reonboard`),
  
  // Pending Tenants (Landlord Invitations)
  getPendingTenantsInvited: () => api.get('/landlord/pending-tenants'),
  invitePendingTenant: (data) => api.post('/landlord/pending-tenants/invite', data),
  resendPendingTenantInvite: (id) => api.post(`/landlord/pending-tenants/${id}/resend-invite`),
  deletePendingTenant: (id) => api.delete(`/landlord/pending-tenants/${id}`),
  
  // Manual Tenants (No email required)
  createManualTenant: (data) => api.post('/landlord/tenants/manual', data),
  getManualTenants: () => api.get('/landlord/tenants/manual'),
  searchManualTenants: (params) => api.get('/landlord/tenants/manual/search', { params }),
  convertManualTenant: (id, email) => api.put(`/landlord/tenants/manual/${id}/convert`, null, { params: { email } }),
  sendInviteToManualTenant: (id) => api.post(`/landlord/tenants/manual/${id}/send-invite`),
  
  // Edit Tenants
  editTenant: (id, data) => api.put(`/landlord/tenants/${id}/edit`, data),
  editManualTenant: (id, data) => api.put(`/landlord/tenants/manual/${id}/edit`, data),
  editPendingTenant: (id, data) => api.put(`/landlord/pending-tenants/${id}/edit`, data),
  uploadTenantPhoto: (id, formData) => api.post(`/landlord/tenants/${id}/upload-photo`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  sendEmailToTenant: (data) => api.post('/landlord/send-email', data),
  createForm: (templateId, tenantId) => api.post(`/landlord/forms/create?template_id=${templateId}&tenant_id=${tenantId}`),
  deleteForm: (formId) => api.delete(`/landlord/forms/${formId}`),
  getRegistrationLink: () => api.get('/landlord/registration-link'),
  getGeneralFormLink: () => api.get('/landlord/general-form-link'),
  getLandlordForms: (tenantId) => api.get(`/landlord/forms${tenantId ? `?tenant_id=${tenantId}` : ''}`),

  // Tenant Documents (replaces Forms section)
  listTenantDocuments: (tenantId) => api.get(`/landlord/tenants/${tenantId}/documents`),
  uploadTenantDocument: (tenantId, formData) => api.post(`/landlord/tenants/${tenantId}/documents/upload`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  createTenantNote: (tenantId, data) => api.post(`/landlord/tenants/${tenantId}/documents/note`, data),
  getTenantDocument: (docId) => api.get(`/landlord/documents/${docId}`),
  updateTenantDocument: (docId, data) => api.put(`/landlord/documents/${docId}`, data),
  deleteTenantDocument: (docId) => api.delete(`/landlord/documents/${docId}`),
  resendLeaseToTenant: (tenantId) => api.post(`/landlord/tenants/${tenantId}/resend-lease`),
  
  // Manual Payments
  createManualPayment: (formData) => api.post('/landlord/payments/manual', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  getManualPayments: () => api.get('/landlord/payments/manual'),
  
  // Payments
  getPayments: (status) => api.get(`/landlord/payments${status ? `?status=${status}` : ''}`),
  verifyPayment: (id) => api.put(`/landlord/payments/${id}/verify`),
  rejectPayment: (id, reason) => api.put(`/landlord/payments/${id}/reject?reason=${encodeURIComponent(reason)}`),
  editPayment: (id, data) => api.put(`/landlord/payments/${id}/edit`, data),
  deletePayment: (id) => api.delete(`/landlord/payments/${id}`),
  downloadReceipt: (paymentId) => api.get(`/landlord/receipts/${paymentId}/download`, { responseType: 'blob' }),
  
  // Expenses
  getExpenses: (propertyId) => api.get(`/landlord/expenses${propertyId ? `?property_id=${propertyId}` : ''}`),
  createExpense: (data) => api.post('/landlord/expenses', data),
  deleteExpense: (id) => api.delete(`/landlord/expenses/${id}`),
  editExpense: (id, data) => api.put(`/landlord/expenses/${id}`, data),
  stopRecurringExpense: (id) => api.post(`/landlord/expenses/${id}/stop-recurring`),
  processRecurringExpenses: () => api.post('/landlord/expenses/process-recurring'),
  getBookkeeping: (months) => api.get(`/landlord/bookkeeping${months ? `?months=${months}` : ''}`),
  exportBookkeeping: (format, months) => api.get(`/landlord/bookkeeping/export?format=${format}&months=${months || 6}`, { responseType: 'blob' }),
  
  // Reports
  getReportSummary: () => api.get('/landlord/reports/summary'),
  getComprehensiveReport: () => api.get('/landlord/reports/comprehensive'),
  exportReport: (format) => api.get(`/landlord/reports/export?format=${format}`, { responseType: 'blob' }),
  exportComprehensiveReport: (format, reportType = 'all') => 
    api.get(`/landlord/reports/export-comprehensive?format=${format}&report_type=${reportType}`, { responseType: 'blob' }),
  
  // Settings
  getSettings: () => api.get('/landlord/settings'),
  updateSettings: (data) => api.put('/landlord/settings', data),
  sendTestEmail: () => api.post('/landlord/send-test-email'),
  
  // Lease Terms Management
  getLeaseTerms: () => api.get('/landlord/lease-terms'),
  updateLeaseTerms: (data) => api.put('/landlord/lease-terms', data),
  resetLeaseTerms: () => api.post('/landlord/lease-terms/reset'),
  
  // Support
  getSupportTickets: () => api.get('/landlord/support-tickets'),
  createSupportTicket: (data) => api.post('/landlord/support-tickets', data),
  replyToSupportTicket: (id, data) => api.post(`/landlord/support-tickets/${id}/reply`, typeof data === 'string' ? { message: data } : data),
  uploadSupportAttachment: (formData) => api.post('/messages/upload-attachment', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  // Ratings
  getMyRatings: () => api.get('/landlord/ratings'),
  getRatingsSummary: () => api.get('/landlord/ratings/summary'),
};

// Tenant APIs
export const tenantAPI = {
  getDashboard: () => api.get('/tenant/dashboard'),
  getLease: () => api.get('/tenant/lease'),
  acknowledgeLease: (id) => api.put(`/tenant/lease/${id}/acknowledge`),
  exportLeasePdf: () => api.get('/tenant/lease/export-pdf', { responseType: 'blob' }),
  signLeaseAsTenant: (signatureImage) => api.post('/tenant/lease/sign', { signature_image: signatureImage }),
  getPayments: () => api.get('/tenant/payments'),
  submitPayment: (formData) => api.post('/tenant/payments', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  getReceipts: () => api.get('/tenant/receipts'),
  downloadReceipt: (paymentId) => api.get(`/tenant/receipts/${paymentId}/download`, { responseType: 'blob' }),
  getLandlordPaymentMethods: () => api.get('/tenant/landlord-payment-methods'),
  // Ratings
  getPendingRatings: () => api.get('/tenant/pending-ratings'),
  submitRating: (data) => api.post('/tenant/ratings', data),
  getMyRatings: () => api.get('/tenant/ratings'),

  // Documents (read-only view of landlord-shared docs)
  listMyDocuments: () => api.get('/tenant/documents'),
  getMyDocument: (docId) => api.get(`/tenant/documents/${docId}`),
};

// Rating APIs
export const ratingAPI = {
  getLandlordPublicRatings: (landlordId) => api.get(`/landlord/${landlordId}/public-ratings`),
};

// Notification APIs
export const notificationAPI = {
  getAll: () => api.get('/notifications'),
  getNotifications: () => api.get('/notifications'),
  getUnreadCount: () => api.get('/notifications/unread-count'),
  markAsRead: (id) => api.put(`/notifications/${id}/read`),
  markAllAsRead: () => api.put('/notifications/mark-all-read'),
  deleteNotification: (id) => api.delete(`/notifications/${id}`),
  triggerCheck: () => api.post('/notifications/trigger-check'),
};

// Messaging APIs
export const messageAPI = {
  getMessages: () => api.get('/messages'),
  getConversations: () => api.get('/messages/conversations'),
  getConversation: (partnerId) => api.get(`/messages/${partnerId}`),
  sendMessage: (data) => api.post('/messages', data),
  getContacts: () => api.get('/messages/contacts'),
  announce: (data) => api.post('/messages/announce', data),
  uploadAttachment: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/messages/upload-attachment', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

// Profile APIs
export const profileAPI = {
  getProfile: () => api.get('/profile'),
  updateProfile: (formData) => api.put('/profile', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  uploadAvatar: (formData) => api.post('/profile/avatar', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  deleteAvatar: () => api.delete('/profile/avatar'),
  deleteAccount: () => api.delete('/profile/account'),
  changePassword: (currentPassword, newPassword) => api.post('/profile/change-password', {
    current_password: currentPassword,
    new_password: newPassword,
  }),
  // Language preference
  getLanguagePreference: () => api.get('/profile/language'),
  updateLanguagePreference: (language) => api.put('/profile/language', { language }),
};

// Utility
export const getUploadUrl = (path) => {
  if (!path) return '';
  // Already a full external URL (S3 presigned or other)
  if (path.startsWith('http://') || path.startsWith('https://')) return path;
  // Already a full API path (e.g., /api/uploads/... or /api/files/s3/...)
  if (path.startsWith('/api/')) return `${BACKEND_URL}${path}`;
  // S3 keys (e.g., "avatars/abc.jpg", "form_uploads/xyz.pdf") — route through S3 proxy
  return `${API_BASE}/files/s3/${path}`;
};

// Nav badge counts + support read-state (shared across roles)
export const badgeAPI = {
  getCounts: () => api.get('/badge-counts'),
  getSupportUnread: () => api.get('/support-tickets/unread-count'),
  markSupportRead: (ticketId) => api.post(`/support-tickets/${ticketId}/mark-read`),
};

// Public API (no auth required)
export const publicAPI = {  getProperties: (limit = 8) => api.get(`/public/properties?limit=${limit}`),
  getStats: () => api.get('/public/stats'),
  getTestimonials: () => api.get('/public/testimonials'),
  getPlans: () => api.get('/subscriptions/plans'),
  submitContact: (data) => api.post('/public/contact', data),
  getForm: (token) => api.get(`/forms/${token}`),
  submitForm: (token, data) => api.post(`/forms/${token}/submit`, data),
  uploadFormFile: (token, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/forms/${token}/upload`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  getGeneralForm: (code) => api.get(`/forms/general/${code}`),
  submitGeneralForm: (code, data) => api.post(`/forms/general/${code}/submit`, data),
};

export default api;
